import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import isNil from 'lodash/isNil';
import metricsStore from './metricsStore';
import dimensionsStore from './dimensionsStore';
import { CURRENT_PRODUCT_MODE, CONTEXT_MAP } from '@src/utils/constants';
import {
  buildFlowUsecases,
  buildFrustrationSignalUsecases,
  buildEventUsecase,
  buildUsecases,
} from '@components/common/Dashboard/UseCases/utils';

type State = {
  presets: any;
  flowPresets: any;
  frustrationSignalPresets: any;
  eventPreset: any;
};

type Action = {
  setPresets: () => void;
  setFlowPresets: (presets: State['flowPresets']) => void;
  setFrustrationSignalPresets: (presets: State['frustrationSignalPresets']) => void;
  setEventPreset: (presets: State['eventPreset']) => void;
  getPreset: (key?: string | number) => any;
};

const initialState: State = {
  presets: {},
  flowPresets: {},
  frustrationSignalPresets: {},
  eventPreset: {},
};

const presetStore = create(
  devtools<State & Action>(
    (set: any, get: any) => ({
      ...initialState,
      setPresets: () =>
        set(() => {
          const { metricsMeta } = metricsStore.getState();
          const { dimensionsMeta } = dimensionsStore.getState();
          const context = CONTEXT_MAP[CURRENT_PRODUCT_MODE];
          const presets = buildUsecases(metricsMeta[context], dimensionsMeta);
          return { presets };
        }),
      setFlowPresets: (flowConfigMeta) =>
        set(() => {
          const { metricsMeta } = metricsStore.getState();
          const { dimensionsMeta } = dimensionsStore.getState();
          const context = CONTEXT_MAP[CURRENT_PRODUCT_MODE];
          const flowPresets = buildFlowUsecases(
            metricsMeta[context],
            dimensionsMeta,
            flowConfigMeta,
          );
          return { flowPresets };
        }),
      setFrustrationSignalPresets: (frustrationSignalConfigMeta) =>
        set(() => {
          const { metricsMeta } = metricsStore.getState();
          const { dimensionsMeta } = dimensionsStore.getState();
          const context = CONTEXT_MAP[CURRENT_PRODUCT_MODE];
          const frustrationSignalPresets = buildFrustrationSignalUsecases(
            metricsMeta[context],
            dimensionsMeta,
            frustrationSignalConfigMeta,
          );
          return { frustrationSignalPresets };
        }),
      setEventPreset: (eventConfigMeta) =>
        set(() => {
          const { metricsMeta } = metricsStore.getState();
          const { dimensionsMeta } = dimensionsStore.getState();
          const context = CONTEXT_MAP[CURRENT_PRODUCT_MODE];
          const eventPreset = buildEventUsecase(
            metricsMeta[context],
            dimensionsMeta,
            eventConfigMeta,
          );
          return { eventPreset };
        }),
      getPreset: (key?: string | number) => {
        if (isNil(key)) return undefined;

        const { presets, flowPresets, frustrationSignalPresets } = get();

        return presets[key] ?? flowPresets[key] ?? frustrationSignalPresets[key];
      },
    }),
    { name: 'presetStore' },
  ),
);

export default presetStore;
